// Implements: F-UIX-018
/**
 * Who the backoffice shows a section to (F-UIX-018), read from the server's permission answer
 * on the session's scopes (UIX.5).
 *
 * This decides what the NAVIGATION offers and which section routes render; it authorises
 * nothing. Every backoffice read is decided again by the server, which answers a reader it
 * refuses with the same `404` as a path that does not exist.
 *
 * An audience is either everybody (My usage) or the actions that open the section: it is
 * shown to a person when ANY of their scopes carries ANY of those actions in its
 * `permissions`, the answer the server's own decision point computed for that scope. No role
 * name is compared here, so the client never becomes a second place where access is decided
 * (an architecture guard keeps role literals out of the backoffice).
 */

import type { BackofficeScope, ScopePermission } from "../../../types/api";

/** Where the backoffice lives; every section is a segment under it. */
export const BACKOFFICE_PATH = "/backoffice";

/** Everybody, or the server actions of which any one opens the section. */
export type BackofficeAudience = "everyone" | readonly ScopePermission[];

/** Whether the server allows `action` over `scope`; a scope with no answer allows nothing. */
export function permits(scope: BackofficeScope, action: ScopePermission): boolean {
  return (scope.permissions ?? []).includes(action);
}

/** Whether a person holding `scopes` is shown a section meant for `audience`. */
export function admits(audience: BackofficeAudience, scopes: readonly BackofficeScope[]): boolean {
  if (audience === "everyone") return true;
  return scopes.some((scope) => audience.some((action) => permits(scope, action)));
}
